/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// react
import { useRef, useEffect, useState } from 'react';
import isNil from 'lodash/isNil';

// mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';

// Styles
import classes from './EditOrganization.module.css';

// local components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';
import { EditOrganizationForm } from './EditOrganizationForm/EditOrganizationForm';

// store
import useStore from '@store/index';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { TFormValuesOrganization } from '../../types';
import { REGEX_STRING } from '@config/constants/regex';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const EditOrganization = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		organizationsSelectedRow,
	} = useStore();

	// Validators
	const form = useForm<TFormValuesOrganization>({
		initialValues: {
			name: '',
			countryCode: null,
			place: '',
			legalIdentifier: '',
			owner: null,
			obfuscateIPFS: '',
			logo: null,
		},
		validate: {
			name: (value) =>
				REGEX_STRING.test(value)
					? null
					: t('global.nameMincharacter'),

			owner: isNotEmpty(t('global.ownerRequired')),
			countryCode: isNotEmpty(
				t('global.countryRequired')
			),
			place: (value) =>
				value && REGEX_STRING.test(value)
					? null
					: t('global.placeMincharacter'),
		},
	});

	useEffect(() => {
		if (!isNil(organizationsSelectedRow)) {
			form.setValues({
				name: organizationsSelectedRow.name,
				countryCode: `${organizationsSelectedRow?.location?.countryCode || null}`,
				place: `${organizationsSelectedRow?.location?.place || null}`,
				owner: `${organizationsSelectedRow?.owner?.id || null}`,
				...(organizationsSelectedRow?.logo
					? { logo: organizationsSelectedRow.logo }
					: {
							logo: null,
						}),
			});

			form.resetDirty();
		}
	}, [organizationsSelectedRow]);

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t(
						'organizations.backToAllOrganizationsSure'
					)}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('organizations.backToAllOrganizations')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<EditOrganizationForm
								open={props.opened}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
